import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { switchMap, map, catchError, take, finalize } from 'rxjs/operators';
import * as BreakdownReasonActions from './breakdown-reasons.actions';
import { TBreakdownReasonsInfoActionType } from './breakdown-reasons.models';
import { FactoryFacade } from '../factory/factory.facade';
import { BreakdownReasonsFacade } from './breakdown-reasons.facade';

import { AppNotificationUtilClass, MachineMaintenanceDataService } from '@monorepo/services';
import { TREQUEST_EDIT_BREAKDOWN_REASON } from '@monorepo/types';

@Injectable({ providedIn: 'root' })
export class BreakdownReasonsEffects {
  constructor(
    private actions$: Actions,
    private mmDataService: MachineMaintenanceDataService,
    private factoryFacade: FactoryFacade,
    private breakdownReasonFacade: BreakdownReasonsFacade,
    private APIHelper: AppNotificationUtilClass,
  ) {}

  getBreakdownReasonsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TBreakdownReasonsInfoActionType.GetBreakdownReasonsList),
      switchMap((action: BreakdownReasonActions.GetBreakdownReasonsList) =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return [action, factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        this.breakdownReasonFacade.getPagination().pipe(
          take(1),
          map((pageInfo) => {
            return [...args, pageInfo] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, factoryId, pagination]) =>
        this.mmDataService
          .fetchBreakdownReasonListData({
            factoryId,
            page: pagination.pageNo,
            itemsPerPage: pagination.pageSize,
            selectedFilters: action.selectedFilters,
          })
          .pipe(
            finalize(() => {
              this.APIHelper.dismissLoadingV2(loaderEl);
            }),
            map((res) => new BreakdownReasonActions.GetBreakdownReasonsListSuccess(res, action.fetchReason)),
            catchError(() => {
              return of(new BreakdownReasonActions.GetBreakdownReasonsListFailure());
            }),
          ),
      ),
    ),
  );

  getBreakdownReasonsDropDown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TBreakdownReasonsInfoActionType.GetBreakdownReasonsListFilterDropDown),
      switchMap(() =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return factoryId;
          }),
        ),
      ),
      switchMap((factoryId) =>
        from(this.mmDataService.getBreakdownReasonListFilterDropDownValues(factoryId)).pipe(
          map((data) => new BreakdownReasonActions.GetBreakdownReasonsFilterDropDownInfoSuccess(data)),
          catchError(() => {
            return of(new BreakdownReasonActions.GetBreakdownReasonsFilterDropDownInfoFailure());
          }),
        ),
      ),
    ),
  );

  editBreakdownReasons$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TBreakdownReasonsInfoActionType.EditBreakdownReasons),
      switchMap((action: BreakdownReasonActions.EditBreakdownReasons) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.editBreakdownReason(action.payload).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.breakdownReasonFacade.resetBreakdownReasonsListInfo('ACTION');
            return new BreakdownReasonActions.EditBreakdownReasonsSuccess();
          }),
          catchError(() => {
            return of(new BreakdownReasonActions.EditBreakdownReasonsFailure());
          }),
        ),
      ),
    ),
  );

  deleteBreakdownReasons$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TBreakdownReasonsInfoActionType.DeleteBreakdownReasons),
      switchMap((action: BreakdownReasonActions.DeleteBreakdownReason) =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return [action, factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, factoryId]) =>
        from(
          this.mmDataService.deleteBreakdownReason({
            factoryId,
            breakdownReasonIds: action.breakdownReasonIds,
          }),
        ).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.breakdownReasonFacade.resetBreakdownReasonsListInfo('ACTION');
            return new BreakdownReasonActions.DeleteBreakdownReasonSuccess();
          }),
          catchError(() => {
            return of(new BreakdownReasonActions.DeleteBreakdownReasonFailure());
          }),
        ),
      ),
    ),
  );
}
